import React from 'react';
import styled from 'styled-components';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  margin: 20px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ErrorIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  
  svg {
    width: 20px;
    height: 20px;
    color: #dc2626;
  }
`;

const ModalTitle = styled.h2`
  color: #dc2626;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const ModalDescription = styled.p`
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const SkuList = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  max-height: 200px;
  overflow-y: auto;
`;

const SkuListTitle = styled.h4`
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const SkuItem = styled.div`
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #dc2626;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SolutionSection = styled.div`
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
`;

const SolutionTitle = styled.h4`
  color: #1e40af;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  
  svg {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }
`;

const SolutionList = styled.ul`
  color: #374151;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 6px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background-color: #2563eb;
    color: white;
    border: 1px solid #2563eb;
    
    &:hover {
      background-color: #1d4ed8;
      border-color: #1d4ed8;
    }
  ` : `
    background-color: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background-color: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

// Component Props
interface DuplicateSkuModalProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateSkus: string[];
  onFixAndRetry: () => void;
}

const DuplicateSkuModal: React.FC<DuplicateSkuModalProps> = ({
  isOpen,
  onClose,
  duplicateSkus,
  onFixAndRetry
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ErrorIcon>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </ErrorIcon>
          <div>
            <ModalTitle>Duplicate SKU Codes Detected</ModalTitle>
          </div>
        </ModalHeader>

        <ModalDescription>
          Your upload contains SKU codes that already exist in the system. Duplicate SKUs are not allowed to maintain data integrity.
        </ModalDescription>

        <SkuList>
          <SkuListTitle>
            Duplicate SKU Codes ({duplicateSkus.length}):
          </SkuListTitle>
          {duplicateSkus.map((sku, index) => (
            <SkuItem key={index}>
              {sku}
            </SkuItem>
          ))}
        </SkuList>

        <SolutionSection>
          <SolutionTitle>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            How to Fix This Issue:
          </SolutionTitle>
          <SolutionList>
            <li><strong>Remove duplicate rows</strong> from your CSV file that contain these SKU codes</li>
            <li><strong>Check for typos</strong> - similar SKU codes might be duplicates (e.g., "ABC123" vs "ABC-123")</li>
            <li><strong>Update existing records</strong> instead of creating new ones if these SKUs should be modified</li>
            <li><strong>Use unique SKU codes</strong> for new products to avoid conflicts</li>
            <li><strong>Review your data</strong> to ensure each SKU appears only once in your file</li>
          </SolutionList>
        </SolutionSection>

        <ButtonContainer>
          <Button variant="secondary" onClick={onClose}>
            Cancel Upload
          </Button>
          <Button variant="primary" onClick={onFixAndRetry}>
            Fix File & Try Again
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default DuplicateSkuModal; 